import React from "react"
import ApartmentIcon from "@mui/icons-material/Apartment"
import HomeIcon from "@mui/icons-material/Home"
import LandscapeIcon from "@mui/icons-material/Landscape"
import StarOutlineIcon from "@mui/icons-material/StarOutline"
import StorefrontIcon from "@mui/icons-material/Storefront"
import type {PropertyType} from "@casas-diamante/types"
import type {CondoPriceAnalysisResponse} from "@/api/condo"

const PLACEHOLDER = "-"

const isFiniteNumber = (value: unknown): value is number =>
   typeof value === "number" && Number.isFinite(value)

const formatMoney = (value: unknown) =>
   isFiniteNumber(value)
      ? `$${value.toLocaleString("en-US", {
           minimumFractionDigits: 0,
           maximumFractionDigits: 0,
        })}`
      : PLACEHOLDER

const formatCount = (value: unknown) =>
   isFiniteNumber(value) ? value.toLocaleString("es-MX") : PLACEHOLDER

const propertyTypeIcons: Record<PropertyType, React.ReactNode> = {
   Departamento: <ApartmentIcon fontSize="small" />,
   Terreno: <LandscapeIcon fontSize="small" />,
   Casa: <HomeIcon fontSize="small" />,
   "Local/Oficina": <StorefrontIcon fontSize="small" />,
   Especial: <StarOutlineIcon fontSize="small" />,
}

function CondoPriceAnalysisTable({
   analysis,
   loading,
   error,
}: {
   analysis?: CondoPriceAnalysisResponse
   loading: boolean
   error: boolean
}) {
   return (
      <section className="mb-8 rounded border border-gray-200 bg-white p-4 shadow-sm">
         <h2 className="mb-4 text-blue">
            Análisis de Precios del Condominio {analysis?.condoName || ""}
         </h2>
         <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full border-collapse text-sm">
               <thead>
                  <tr className="bg-blue text-white">
                     <th className="border border-blue px-3 py-3 text-center">Tipo de inmueble</th>
                     <th className="border border-blue px-3 py-3 text-center">
                        Precio promedio m² Condominio
                     </th>
                     <th className="border border-blue px-3 py-3 text-center">
                        Precio promedio m² Categoría {analysis?.categoryLabel || PLACEHOLDER}
                     </th>
                     <th className="border border-blue px-3 py-3 text-center">% vs su categoría</th>
                     <th className="border border-blue px-3 py-3 text-center">
                        Total inmuebles en condominio
                     </th>
                     <th className="border border-blue px-3 py-3 text-center">
                        Total inmuebles en categoría
                     </th>
                  </tr>
               </thead>
               <tbody>
                  {loading && [0, 1, 2, 3, 4].map((row) => (
                     <tr key={row} className="bg-white">
                        {[0, 1, 2, 3, 4, 5].map((cell) => (
                           <td key={cell} className="border border-gray-200 px-3 py-4">
                              <span className="block h-3 animate-pulse bg-gray-100" />
                           </td>
                        ))}
                     </tr>
                  ))}
                  {!loading && analysis?.rows.map((row) => {
                     const comparisonClass =
                        row.comparison?.direction === "below"
                           ? "text-green"
                           : row.comparison?.direction === "above"
                           ? "text-red"
                           : "text-blue"
                     const comparisonIcon =
                        row.comparison?.direction === "below"
                           ? "↓"
                           : row.comparison?.direction === "above"
                           ? "↑"
                           : ""

                     return (
                        <tr key={row.propertyType} className="bg-white">
                           <td className="border border-gray-200 px-3 py-3 font-bold text-blue">
                              <span className="flex items-center gap-2">
                                 {propertyTypeIcons[row.propertyType]}
                                 {row.label}
                              </span>
                           </td>
                           <td className="border border-gray-200 px-3 py-3 text-center font-bold">
                              {formatMoney(row.condoPricePerMeter)}
                           </td>
                           <td className="border border-gray-200 px-3 py-3 text-center font-bold">
                              {formatMoney(row.categoryPricePerMeter)}
                           </td>
                           <td className="border border-gray-200 px-3 py-3 text-center">
                              {row.comparison ? (
                                 <span className={`font-bold ${comparisonClass}`}>
                                    {comparisonIcon && `${comparisonIcon} `}
                                    {row.comparison.percentDifference.toLocaleString(undefined, {
                                       minimumFractionDigits: 2,
                                       maximumFractionDigits: 2,
                                    })}
                                    %
                                    <span className="block text-xs font-normal text-blue">
                                       {row.comparison.direction === "below"
                                          ? "por debajo"
                                          : row.comparison.direction === "above"
                                          ? "por encima"
                                          : "igual"}
                                    </span>
                                 </span>
                              ) : (
                                 PLACEHOLDER
                              )}
                           </td>
                           <td className="border border-gray-200 px-3 py-3 text-center font-bold">
                              {formatCount(row.condoPropertyCount)}
                           </td>
                           <td className="border border-gray-200 px-3 py-3 text-center font-bold">
                              {formatCount(row.categoryPropertyCount)}
                           </td>
                        </tr>
                     )
                  })}
                  {!loading && error && (
                     <tr>
                        <td className="border border-gray-200 px-3 py-4 text-center text-red" colSpan={6}>
                           No se pudo cargar el análisis de precios.
                        </td>
                     </tr>
                  )}
               </tbody>
            </table>
         </div>
         <p className="mt-4 text-xs text-blue">Inmuebles Activos y Disponibles</p>
      </section>
   )
}

export default CondoPriceAnalysisTable
